import { toInputDate } from "@/utils/dateUtils";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { openFilePicker } from "@/lib/native/openFilePicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Upload,
  Loader2,
  X,
  ChevronLeft,
  ChevronRight,
  Plus,
  PenLine,
} from "lucide-react";
import { NativeImage } from "@/components/native/NativeImage";
import { useMedia } from "@/hooks/useMedia";
import { compressImageForFullSize } from "@/utils/imageCompression";
import type {
  DealerPortalVehicleDocument,
  DealerPortalVehicleServiceDetail,
} from "@/types/dealerPortal";
import { useLanguage } from "@/context/LanguageContext";
import { vehicleCardUILabels } from "@/i18n";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const IMAGE_CATEGORIES = [
  "EXTERIOR",
  "INTERIOR",
  "DAMAGE",
  "SERVICE",
  "TIRES",
] as const;
export type DealerPortalImageCategory = (typeof IMAGE_CATEGORIES)[number];

const MAX_EXTERIOR = 15;
const MAX_INTERIOR = 15;
const MAX_DAMAGE = 10;
const MAX_PER_UPLOAD = 5;

interface DealerPortalVehicleImagesProps {
  vehicleId: string;
  documents: DealerPortalVehicleDocument[];
  onUpload: (params: {
    vehicleId: string;
    file: File;
    imageCategory: DealerPortalImageCategory;
  }) => void;
  onDelete: (params: { vehicleId: string; mediaId: string }) => void;
  isUploading?: boolean;
  deletingMediaId?: string | null;
  /** Label for the thumbnail in Exterior (e.g. "Huvudbild"). Shown on first exterior image. */
  thumbnailLabel?: string;
  /** Servicedetaljer – visas i Service-sektionen med knapp "Lägg till servicedetaljer" */
  serviceDetails?: DealerPortalVehicleServiceDetail[];
  onAddServiceDetail?: (data: {
    date: string;
    mileage: number;
    serviceType: string;
    notes?: string | null;
  }) => Promise<void>;
  onRemoveServiceDetail?: (detailId: string) => Promise<void>;
  onUpdateServiceDetail?: (
    detailId: string,
    data: {
      date: string;
      mileage: number;
      serviceType: string;
      notes?: string | null;
    },
  ) => Promise<void>;
  /** Uppdatera skadekommentar för ett dokument (imageCategory = DAMAGE) */
  onUpdateDamageComment?: (
    documentId: string,
    comment: string | null,
  ) => Promise<void>;
}

function Thumb({
  mediaId,
  alt,
  className,
  onDelete,
  onImageClick,
  isDeleting,
  thumbnailLabel,
  variant = "regular",
}: {
  mediaId: string;
  alt: string;
  className?: string;
  onDelete?: () => void;
  onImageClick?: () => void;
  isDeleting?: boolean;
  thumbnailLabel?: string;
  /** "thumbnail" = blue border + blue badge (Exterior huvudbild), "regular" = gray border */
  variant?: "thumbnail" | "regular";
}) {
  const { getDownloadUrlAsync } = useMedia();
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDownloadUrlAsync({ mediaId, expiresIn: 3600 })
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mediaId, getDownloadUrlAsync]);

  if (!src) {
    return (
      <div
        className={`bg-muted animate-pulse rounded border flex items-center justify-center h-24 ${className ?? ""}`}
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isThumbnail = variant === "thumbnail";
  return (
    <div
      className={`relative group rounded overflow-hidden ${className ?? ""} ${onImageClick ? "cursor-pointer" : ""}`}
      onClick={onImageClick}
      role={onImageClick ? "button" : undefined}
      tabIndex={onImageClick ? 0 : undefined}
      title={onImageClick ? "Visa i full storlek" : undefined}
      aria-label={onImageClick ? "Visa bild i full storlek" : undefined}
      onKeyDown={
        onImageClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onImageClick();
              }
            }
          : undefined
      }
    >
      {isDeleting && (
        <div className="absolute inset-0 bg-black/50 rounded flex items-center justify-center z-10">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
        </div>
      )}
      <NativeImage
        src={src}
        alt={alt}
        className={`w-full h-24 object-cover ${onImageClick ? "group-hover:opacity-95" : ""} ${isThumbnail ? "rounded border-2 border-blue-500" : "rounded border"} ${isDeleting ? "opacity-50" : ""}`}
      />
      {thumbnailLabel && isThumbnail && (
        <span className="absolute -top-1 -left-1 bg-blue-500 text-white text-xs font-medium px-1.5 py-0.5 rounded shadow">
          {thumbnailLabel}
        </span>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isDeleting}
          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10 disabled:opacity-50"
          aria-label="Ta bort bild"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

/** Lightbox: visar bild större. Använder alltid preferFullSize så backend returnerar FULL_SIZE-URL
 * (dealer-portal: images/fullsize; car test: fullsize). Sidan visar PREVIEW; popup = FULL_SIZE. */
function ImageLightbox({
  items,
  initialIndex,
  onClose,
}: {
  items: { mediaId: string; alt: string }[];
  initialIndex: number;
  onClose: () => void;
}) {
  const { getDownloadUrlAsync } = useMedia();
  const [index, setIndex] = useState(initialIndex);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  const item = items[index];

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    setLoading(true);
    getDownloadUrlAsync({
      mediaId: item.mediaId,
      expiresIn: 3600,
      preferFullSize: true,
    })
      .then((u) => {
        if (!cancelled) {
          setUrl(u);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item?.mediaId, getDownloadUrlAsync]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + items.length) % items.length);
  }, [items.length]);
  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (items.length <= 1) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [items.length, onClose, goPrev, goNext]);

  if (items.length === 0) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showCloseButton={true}
        className="max-w-[95vw] max-h-[95dvh] w-full h-full p-0 gap-0 bg-black/95 border-0 overflow-hidden"
        onPointerDownOutside={onClose}
        onEscapeKeyDown={onClose}
      >
        <div
          ref={contentRef}
          className="relative flex items-center justify-center w-full h-full min-h-[60vh] group"
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <Loader2 className="h-10 w-10 animate-spin text-white" />
            </div>
          )}
          {url && (
            <NativeImage
              src={url}
              alt={item.alt}
              className={`max-w-full max-h-[90dvh] w-auto h-auto object-contain select-none ${loading ? "opacity-0" : "opacity-100"} transition-opacity`}
              draggable={false}
              style={{ pointerEvents: "none" }}
            />
          )}
          {items.length > 1 && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/30 hover:bg-black/60 text-white border-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  goPrev();
                }}
                aria-label="Föregående bild"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/30 hover:bg-black/60 text-white border-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  goNext();
                }}
                aria-label="Nästa bild"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
          {items.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/70 text-xs font-medium bg-black/30 px-2 py-1 rounded">
              {index + 1} / {items.length}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DealerPortalVehicleImages({
  vehicleId,
  documents,
  onUpload,
  onDelete,
  isUploading = false,
  deletingMediaId = null,
  thumbnailLabel = "Huvudbild",
  serviceDetails = [],
  onAddServiceDetail,
  onRemoveServiceDetail,
  onUpdateServiceDetail,
  onUpdateDamageComment,
}: DealerPortalVehicleImagesProps) {
  const { language } = useLanguage();
  const vc = vehicleCardUILabels(language);
  const categoryLabels: Record<DealerPortalImageCategory, string> = {
    EXTERIOR: vc.exterior ?? "Exteriör",
    INTERIOR: vc.interior ?? "Interiör",
    DAMAGE: vc.damage ?? "Skador",
    SERVICE: vc.service ?? "Service",
    TIRES: vc.tires ?? "Däck",
  };

  const [lightbox, setLightbox] = useState<{
    items: { mediaId: string; alt: string }[];
    index: number;
  } | null>(null);
  const [showServiceDetailDialog, setShowServiceDetailDialog] = useState(false);
  const [editingServiceDetail, setEditingServiceDetail] =
    useState<DealerPortalVehicleServiceDetail | null>(null);
  const [newServiceDetail, setNewServiceDetail] = useState({
    date: toInputDate(new Date()),
    mileage: "",
    serviceType: "",
    notes: "",
  });
  const [editingDamageDocId, setEditingDamageDocId] = useState<string | null>(
    null,
  );
  const [draftDamageComment, setDraftDamageComment] = useState("");
  const [isCompressing, setIsCompressing] = useState(false);

  const isBusy = isUploading || isCompressing;

  const openPicker = async (
    category: DealerPortalImageCategory,
    multiple: boolean,
    _isThumbnail: boolean,
  ) => {
    const files = await openFilePicker({ accept: "image/*", multiple });
    if (files.length === 0) return;
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    handleFileChange(
      { target: { files: dt.files } } as React.ChangeEvent<HTMLInputElement>,
      category,
    );
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    category: DealerPortalImageCategory,
  ) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length > MAX_PER_UPLOAD) {
      toast.error("Fel", { description: vc.max5ImagesAtOnce ?? "Max 5 bilder åt gången" });
      return;
    }
    const imageDocs = documents.filter((d) => {
      const arr = Array.isArray(d.media) ? d.media : d.media ? [d.media] : [];
      return arr.some((m) => m.mimeType?.startsWith("image/"));
    });
    const byCat = (cat: DealerPortalImageCategory) =>
      imageDocs.filter((d) => (d.imageCategory ?? "").toUpperCase() === cat);
    const currentCount = byCat(category).length;
    const maxForSection =
      category === "EXTERIOR"
        ? MAX_EXTERIOR - 1
        : category === "INTERIOR"
          ? MAX_INTERIOR
          : category === "DAMAGE"
            ? MAX_DAMAGE
            : 10;
    if (currentCount + imageFiles.length > maxForSection) {
      const allowed = Math.max(0, maxForSection - currentCount);
      toast.error("Fel", {
        description: (
          vc.maxImagesExceeded ??
          "Max {remaining} bilder kvar (max {max}, du har {current})"
        )
          .replace("{remaining}", String(allowed))
          .replace("{max}", String(maxForSection))
          .replace("{current}", String(currentCount)),
      });
      return;
    }
    const toUpload = imageFiles.slice(
      0,
      Math.min(MAX_PER_UPLOAD, maxForSection - currentCount),
    );
    setIsCompressing(true);
    Promise.all(
      toUpload.map((file) =>
        compressImageForFullSize(file)
          .then((compressed) => {
            onUpload({ vehicleId, file: compressed, imageCategory: category });
          })
          .catch(() => {
            onUpload({ vehicleId, file, imageCategory: category });
          }),
      ),
    ).finally(() => setIsCompressing(false));
    if (toUpload.length === 1) {
      toast.success(vc.uploadingImage ?? "Laddar upp...", {
        description: (
          vc.uploadingImageDesc ?? "Komprimerar och laddar upp {name}"
        ).replace("{name}", toUpload[0].name),
      });
    } else if (toUpload.length > 1) {
      toast.success(vc.uploadingImages ?? "Laddar upp bilder...", {
        description: (
          vc.uploadingImagesCountDesc ??
          "Komprimerar och laddar upp {count} bilder"
        ).replace("{count}", String(toUpload.length)),
      });
    }
  };

  const getDisplayMedia = (d: DealerPortalVehicleDocument) => {
    const arr = Array.isArray(d.media) ? d.media : d.media ? [d.media] : [];
    const preview = arr.find(
      (m: { mediaSize?: string | null }) => m.mediaSize === "PREVIEW",
    );
    return preview ?? arr[0] ?? null;
  };
  const getFullSizeMedia = (d: DealerPortalVehicleDocument) => {
    const arr = Array.isArray(d.media) ? d.media : d.media ? [d.media] : [];
    const full = arr.find(
      (m: { mediaSize?: string | null }) =>
        String(m.mediaSize ?? "").toUpperCase() === "FULL_SIZE",
    );
    if (full) return full;
    // If no FULL_SIZE, prefer any non-PREVIEW image (e.g. legacy or different API shape)
    const notPreview = arr.find(
      (m: { mediaSize?: string | null }) =>
        String(m.mediaSize ?? "").toUpperCase() !== "PREVIEW",
    );
    return notPreview ?? arr[0] ?? null;
  };

  // Overview: only show PREVIEW. Exclude documents that have only FULL_SIZE media
  // so we don't get duplicate tiles when backend has one doc per media size.
  const imageDocs = documents
    .filter((d) => {
      const arr = Array.isArray(d.media) ? d.media : d.media ? [d.media] : [];
      const hasPreviewImage = arr.some(
        (m: { mediaSize?: string | null; mimeType?: string }) =>
          m.mediaSize === "PREVIEW" && m.mimeType?.startsWith("image/"),
      );
      const hasOnlyFullSize =
        !hasPreviewImage &&
        arr.some(
          (m: { mediaSize?: string | null; mimeType?: string }) =>
            m.mediaSize === "FULL_SIZE" && m.mimeType?.startsWith("image/"),
        );
      if (hasOnlyFullSize) return false; // skip FULL_SIZE-only doc (duplicate)
      if (hasPreviewImage) {
        const m = getDisplayMedia(d);
        return m?.mimeType?.startsWith("image/");
      }
      const m = getDisplayMedia(d) ?? getFullSizeMedia(d);
      return m?.mimeType?.startsWith("image/"); // legacy: single media or no size
    })
    .filter((d, idx, arr) => arr.findIndex((x) => x.id === d.id) === idx);
  const byCategory = (cat: DealerPortalImageCategory) =>
    imageDocs.filter((d) => {
      const c = (d.imageCategory ?? "").toUpperCase();
      if (c === cat) return true;
      if (cat === "TIRES" && c === "WHEELS") return true; // CarTest uses WHEELS
      return false;
    });
  const uncategorized = imageDocs.filter((d) => {
    const c = (d.imageCategory ?? "").toUpperCase();
    if (!c) return true;
    if (IMAGE_CATEGORIES.includes(d.imageCategory as DealerPortalImageCategory))
      return false;
    if (c === "WHEELS") return false; // treat as TIRES
    return true;
  });

  const exteriorDocs = byCategory("EXTERIOR");
  const thumbnailDoc = exteriorDocs[0] ?? null;
  const regularExteriorDocs = exteriorDocs.slice(1);
  const interiorDocs = byCategory("INTERIOR");
  const damageDocs = byCategory("DAMAGE");

  const toItemPreview = (d: DealerPortalVehicleDocument) => {
    const m = getDisplayMedia(d);
    return m ? { mediaId: m.id, alt: m.originalName ?? d.id } : null;
  };
  const toItemFullSize = (d: DealerPortalVehicleDocument) => {
    const m = getFullSizeMedia(d);
    return m ? { mediaId: m.id, alt: m.originalName ?? d.id } : null;
  };
  const exteriorListFullSize = exteriorDocs
    .map(toItemFullSize)
    .filter(Boolean) as {
    mediaId: string;
    alt: string;
  }[];
  const interiorListFullSize = interiorDocs
    .map(toItemFullSize)
    .filter(Boolean) as {
    mediaId: string;
    alt: string;
  }[];
  const damageListFullSize = damageDocs.map(toItemFullSize).filter(Boolean) as {
    mediaId: string;
    alt: string;
  }[];
  const serviceListFullSize = byCategory("SERVICE")
    .map(toItemFullSize)
    .filter(Boolean) as {
    mediaId: string;
    alt: string;
  }[];
  const tiresListFullSize = byCategory("TIRES")
    .map(toItemFullSize)
    .filter(Boolean) as {
    mediaId: string;
    alt: string;
  }[];
  const uncategorizedListFullSize = uncategorized
    .map(toItemFullSize)
    .filter(Boolean) as {
    mediaId: string;
    alt: string;
  }[];

  return (
    <div className="space-y-6">
      {lightbox && (
        <ImageLightbox
          items={lightbox.items}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">
          {vc.vehicleImages ?? "Fordonets bilder"}
        </h2>
        <p className="text-primary/60">
          {vc.manageImagesForVehicle ?? "Hantera bilder för detta fordon"}
        </p>
      </div>

      {/* Exteriör – samma design som Leadbox VehicleCard > Bilder */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {categoryLabels.EXTERIOR}
            <Badge variant="outline">
              {regularExteriorDocs.length}/{MAX_EXTERIOR - 1}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-3">
              {vc.thumbnail ?? "Thumbnail"}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {thumbnailDoc && (
                <div className="relative">
                  <Thumb
                    mediaId={getDisplayMedia(thumbnailDoc)!.id}
                    alt="Thumbnail"
                    variant="thumbnail"
                    thumbnailLabel={thumbnailLabel}
                    onImageClick={
                      exteriorListFullSize.length > 0
                        ? () =>
                            setLightbox({
                              items: exteriorListFullSize,
                              index: 0,
                            })
                        : undefined
                    }
                    onDelete={() =>
                      getFullSizeMedia(thumbnailDoc)?.id &&
                      onDelete({
                        vehicleId,
                        mediaId: getFullSizeMedia(thumbnailDoc)!.id,
                      })
                    }
                    isDeleting={
                      deletingMediaId === getFullSizeMedia(thumbnailDoc)?.id
                    }
                  />
                </div>
              )}
              {!thumbnailDoc && (
                <button
                  type="button"
                  onClick={() => openPicker("EXTERIOR", false, true)}
                  disabled={isBusy}
                  className="w-full h-24 border-2 border-dashed border-blue-500 rounded flex flex-col items-center justify-center hover:border-blue-600 transition-colors disabled:opacity-50"
                >
                  {isBusy ? (
                    <>
                      <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                      <span className="text-xs text-muted-foreground mt-1">
                        {vc.uploading ?? "Laddar upp..."}
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1 text-center">
                        {vc.uploadThumbnail ?? "Ladda upp thumbnail"}
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-3">{vc.images ?? "Bilder"}</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {regularExteriorDocs.map((doc, i) => (
                <Thumb
                  key={doc.id}
                  mediaId={getDisplayMedia(doc)!.id}
                  alt={getDisplayMedia(doc)?.originalName ?? doc.id}
                  variant="regular"
                  className="w-full"
                  onImageClick={
                    exteriorListFullSize.length > 0
                      ? () =>
                          setLightbox({
                            items: exteriorListFullSize,
                            index: i + 1,
                          })
                      : undefined
                  }
                  onDelete={() =>
                    getFullSizeMedia(doc)?.id &&
                    onDelete({ vehicleId, mediaId: getFullSizeMedia(doc)!.id })
                  }
                  isDeleting={deletingMediaId === getFullSizeMedia(doc)?.id}
                />
              ))}
              {regularExteriorDocs.length <
                MAX_EXTERIOR - (thumbnailDoc ? 1 : 0) && (
                <button
                  type="button"
                  onClick={() => openPicker("EXTERIOR", true, false)}
                  disabled={isBusy}
                  className="w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded flex flex-col items-center justify-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors disabled:opacity-50"
                >
                  {isBusy ? (
                    <>
                      <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                      <span className="text-xs text-muted-foreground mt-1">
                        {vc.uploading ?? "Laddar upp..."}
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500 text-center whitespace-normal px-1">
                        {vc.uploadImagesMax5 ??
                          "Ladda upp bild(er) (Max 5 åt gången)"}
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Interiör */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {categoryLabels.INTERIOR}
            <Badge variant="outline">
              {interiorDocs.length}/{MAX_INTERIOR}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {interiorDocs.map((doc, i) => (
              <Thumb
                key={doc.id}
                mediaId={getDisplayMedia(doc)!.id}
                alt={getDisplayMedia(doc)?.originalName ?? doc.id}
                variant="regular"
                className="w-full"
                onImageClick={
                  interiorListFullSize.length > 0
                    ? () =>
                        setLightbox({ items: interiorListFullSize, index: i })
                    : undefined
                }
                onDelete={() =>
                  getFullSizeMedia(doc)?.id &&
                  onDelete({ vehicleId, mediaId: getFullSizeMedia(doc)!.id })
                }
                isDeleting={deletingMediaId === getFullSizeMedia(doc)?.id}
              />
            ))}
            {interiorDocs.length < MAX_INTERIOR && (
              <button
                type="button"
                onClick={() => openPicker("INTERIOR", true, false)}
                disabled={isBusy}
                className="w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded flex flex-col items-center justify-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors disabled:opacity-50"
              >
                {isBusy ? (
                  <>
                    <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                    <span className="text-xs text-muted-foreground mt-1">
                      {vc.uploading ?? "Laddar upp..."}
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500 text-center whitespace-normal px-1">
                      {vc.uploadImagesMax5 ??
                        "Ladda upp bild(er) (Max 5 åt gången)"}
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Skador */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {categoryLabels.DAMAGE}
            <Badge variant="outline">
              {damageDocs.length}/{MAX_DAMAGE}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {damageDocs.map((doc, i) => (
              <div key={doc.id} className="space-y-1">
                <Thumb
                  mediaId={getDisplayMedia(doc)!.id}
                  alt={getDisplayMedia(doc)?.originalName ?? doc.id}
                  variant="regular"
                  className="w-full"
                  onImageClick={
                    damageListFullSize.length > 0
                      ? () =>
                          setLightbox({ items: damageListFullSize, index: i })
                      : undefined
                  }
                  onDelete={() =>
                    getFullSizeMedia(doc)?.id &&
                    onDelete({ vehicleId, mediaId: getFullSizeMedia(doc)!.id })
                  }
                  isDeleting={deletingMediaId === getFullSizeMedia(doc)?.id}
                />
                {onUpdateDamageComment && (
                  <div className="text-xs">
                    {editingDamageDocId === doc.id ? (
                      <div className="space-y-1">
                        <Textarea
                          value={draftDamageComment}
                          onChange={(e) =>
                            setDraftDamageComment(e.target.value)
                          }
                          placeholder={vc.describeDamage ?? "Beskriv skadan..."}
                          rows={2}
                          className="min-h-[48px]"
                        />
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="default"
                            className="text-xs h-7"
                            onClick={async () => {
                              await onUpdateDamageComment(
                                doc.id,
                                draftDamageComment.trim() || null,
                              );
                              setEditingDamageDocId(null);
                            }}
                          >
                            {vc.save ?? "Spara"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => {
                              setEditingDamageDocId(null);
                              setDraftDamageComment(doc.comment ?? "");
                            }}
                          >
                            {vc.cancel ?? "Avbryt"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingDamageDocId(doc.id);
                          setDraftDamageComment(doc.comment ?? "");
                        }}
                        className="text-left w-full p-1.5 rounded border border-dashed border-muted-foreground/30 hover:bg-muted/50 text-muted-foreground"
                      >
                        {doc.comment ? (
                          <span className="line-clamp-2">{doc.comment}</span>
                        ) : (
                          <span>{vc.addComment ?? "Lägg till kommentar"}</span>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {damageDocs.length < MAX_DAMAGE && (
              <button
                type="button"
                onClick={() => openPicker("DAMAGE", true, false)}
                disabled={isBusy}
                className="w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded flex flex-col items-center justify-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors disabled:opacity-50"
              >
                {isBusy ? (
                  <>
                    <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                    <span className="text-xs text-muted-foreground mt-1">
                      {vc.uploading ?? "Laddar upp..."}
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500 text-center whitespace-normal px-1">
                      {vc.uploadImagesMax5 ??
                        "Ladda upp bild(er) (Max 5 åt gången)"}
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Service – med servicedetaljer + bilder */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {categoryLabels.SERVICE}
            <Badge variant="outline">{byCategory("SERVICE").length}/10</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">
                {vc.serviceDetails ?? "Service-detaljer"}
              </h4>
              {onAddServiceDetail && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingServiceDetail(null);
                    setNewServiceDetail({
                      date: toInputDate(new Date()),
                      mileage: "",
                      serviceType: "",
                      notes: "",
                    });
                    setShowServiceDetailDialog(true);
                  }}
                  className="flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  {vc.addServiceDetails ?? "Lägg till servicedetaljer"}
                </Button>
              )}
            </div>
            {serviceDetails.length > 0 ? (
              <div className="space-y-2">
                {serviceDetails.map((detail) => (
                  <div
                    key={detail.id}
                    className="p-3 border rounded-lg flex justify-between items-start gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{detail.serviceType}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(detail.date).toLocaleDateString(
                          language === "sv" ? "sv-SE" : "en",
                        )}{" "}
                        •{" "}
                        {String(detail.mileage).replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          " ",
                        )}{" "}
                        mil
                      </div>
                      {detail.notes && (
                        <div className="text-sm mt-1">{detail.notes}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {onUpdateServiceDetail && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const d = new Date(detail.date);
                            setNewServiceDetail({
                              date: toInputDate(d),
                              mileage: String(detail.mileage),
                              serviceType: detail.serviceType,
                              notes: detail.notes ?? "",
                            });
                            setEditingServiceDetail(detail);
                            setShowServiceDetailDialog(true);
                          }}
                          aria-label={vc.editServiceDetail ?? "Redigera"}
                          title={vc.editServiceDetail ?? "Redigera"}
                        >
                          <PenLine className="w-4 h-4" />
                        </Button>
                      )}
                      {onRemoveServiceDetail && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveServiceDetail(detail.id)}
                          className="text-destructive hover:text-destructive shrink-0"
                          aria-label="Ta bort"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              onAddServiceDetail && (
                <p className="text-sm text-muted-foreground py-2">
                  {vc.noServiceDetailsYet ?? "Inga servicedetaljer tillagda än"}
                </p>
              )
            )}
          </div>
          <div>
            <h4 className="font-semibold mb-3">{vc.images ?? "Bilder"}</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {byCategory("SERVICE").map((doc, i) => (
                <Thumb
                  key={doc.id}
                  mediaId={getDisplayMedia(doc)!.id}
                  alt={getDisplayMedia(doc)?.originalName ?? doc.id}
                  variant="regular"
                  className="w-full"
                  onImageClick={
                    serviceListFullSize.length > 0
                      ? () =>
                          setLightbox({ items: serviceListFullSize, index: i })
                      : undefined
                  }
                  onDelete={() =>
                    getFullSizeMedia(doc)?.id &&
                    onDelete({ vehicleId, mediaId: getFullSizeMedia(doc)!.id })
                  }
                  isDeleting={deletingMediaId === getFullSizeMedia(doc)?.id}
                />
              ))}
              {byCategory("SERVICE").length < 10 && (
                <button
                  type="button"
                  onClick={() => openPicker("SERVICE", true, false)}
                  disabled={isBusy}
                  className="w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded flex flex-col items-center justify-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors disabled:opacity-50"
                >
                  {isBusy ? (
                    <>
                      <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                      <span className="text-xs text-muted-foreground mt-1">
                        {vc.uploading ?? "Laddar upp..."}
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500 text-center whitespace-normal px-1">
                        {vc.uploadImagesMax5 ??
                          "Ladda upp bild(er) (Max 5 åt gången)"}
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service-detalj dialog */}
      <Dialog
        open={showServiceDetailDialog}
        onOpenChange={(open) => {
          setShowServiceDetailDialog(open);
          if (!open) setEditingServiceDetail(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingServiceDetail
                ? (vc.editServiceDetail ?? "Redigera servicedetalj")
                : (vc.addServiceDetail ?? "Lägg till servicedetalj")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{vc.date ?? "Datum"}</Label>
              <Input
                type="date"
                value={newServiceDetail.date}
                onChange={(e) =>
                  setNewServiceDetail((prev) => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label>{vc.mileageAtService ?? "Miltal vid service"}</Label>
              <Input
                type="number"
                value={newServiceDetail.mileage}
                onChange={(e) =>
                  setNewServiceDetail((prev) => ({
                    ...prev,
                    mileage: e.target.value,
                  }))
                }
                placeholder={vc.enterMileage ?? "Ange miltal"}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{vc.serviceType ?? "Servicetyp"}</Label>
              <Input
                value={newServiceDetail.serviceType}
                onChange={(e) =>
                  setNewServiceDetail((prev) => ({
                    ...prev,
                    serviceType: e.target.value,
                  }))
                }
                placeholder={vc.serviceTypePlaceholder ?? "t.ex. Oljebyte"}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{vc.notes ?? "Anteckningar"}</Label>
              <Textarea
                value={newServiceDetail.notes}
                onChange={(e) =>
                  setNewServiceDetail((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                placeholder={vc.notesPlaceholder ?? "Valfria anteckningar"}
                className="mt-1"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowServiceDetailDialog(false);
                  setEditingServiceDetail(null);
                }}
              >
                {vc.cancel ?? "Avbryt"}
              </Button>
              <Button
                type="button"
                disabled={
                  !newServiceDetail.serviceType.trim() ||
                  !newServiceDetail.mileage ||
                  (!editingServiceDetail && !onAddServiceDetail) ||
                  (editingServiceDetail && !onUpdateServiceDetail)
                }
                onClick={async () => {
                  const payload = {
                    date: newServiceDetail.date,
                    mileage: Number(newServiceDetail.mileage),
                    serviceType: newServiceDetail.serviceType.trim(),
                    notes: newServiceDetail.notes.trim() || null,
                  };
                  if (editingServiceDetail && onUpdateServiceDetail) {
                    await onUpdateServiceDetail(
                      editingServiceDetail.id,
                      payload,
                    );
                  } else if (onAddServiceDetail) {
                    await onAddServiceDetail(payload);
                  }
                  setNewServiceDetail({
                    date: toInputDate(new Date()),
                    mileage: "",
                    serviceType: "",
                    notes: "",
                  });
                  setEditingServiceDetail(null);
                  setShowServiceDetailDialog(false);
                }}
              >
                {editingServiceDetail
                  ? (vc.save ?? "Spara")
                  : (vc.add ?? "Lägg till")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Däck */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {categoryLabels.TIRES}
            <Badge variant="outline">{byCategory("TIRES").length}/10</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {byCategory("TIRES").map((doc, i) => (
              <Thumb
                key={doc.id}
                mediaId={getDisplayMedia(doc)!.id}
                alt={getDisplayMedia(doc)?.originalName ?? doc.id}
                variant="regular"
                className="w-full"
                onImageClick={
                  tiresListFullSize.length > 0
                    ? () => setLightbox({ items: tiresListFullSize, index: i })
                    : undefined
                }
                onDelete={() =>
                  getFullSizeMedia(doc)?.id &&
                  onDelete({ vehicleId, mediaId: getFullSizeMedia(doc)!.id })
                }
                isDeleting={deletingMediaId === getFullSizeMedia(doc)?.id}
              />
            ))}
            {byCategory("TIRES").length < 10 && (
              <button
                type="button"
                onClick={() => openPicker("TIRES", true, false)}
                disabled={isBusy}
                className="w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded flex flex-col items-center justify-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors disabled:opacity-50"
              >
                {isBusy ? (
                  <>
                    <Loader2 className="h-6 w-6 text-primary/70 animate-spin" />
                    <span className="text-xs text-muted-foreground mt-1">
                      {vc.uploading ?? "Laddar upp..."}
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500 text-center whitespace-normal px-1">
                      {vc.uploadImagesMax5 ??
                        "Ladda upp bild(er) (Max 5 åt gången)"}
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        </CardContent>
      </Card>
      {uncategorized.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Övriga bilder</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {uncategorized.map((doc, i) => (
                <Thumb
                  key={doc.id}
                  mediaId={getDisplayMedia(doc)!.id}
                  alt={getDisplayMedia(doc)?.originalName ?? doc.id}
                  variant="regular"
                  className="w-full"
                  onImageClick={
                    uncategorizedListFullSize.length > 0
                      ? () =>
                          setLightbox({
                            items: uncategorizedListFullSize,
                            index: i,
                          })
                      : undefined
                  }
                  onDelete={() =>
                    getFullSizeMedia(doc)?.id &&
                    onDelete({ vehicleId, mediaId: getFullSizeMedia(doc)!.id })
                  }
                  isDeleting={deletingMediaId === getFullSizeMedia(doc)?.id}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
